import React, { useEffect, useState } from 'react';
import { PageHeader, StatCard } from '@/components/primitives';
import { api } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Phone, Video, Send, ClipboardList, BarChart3, Receipt, Users, IndianRupee, TrendingUp, Clock } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const inr = (n) => `\u20B9 ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function AnalyticsPage() {
  const [scope, setScope] = useState('week');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [companyId, setCompanyId] = useState('all');
  const [userId, setUserId] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [cost, setCost] = useState(null);

  useEffect(() => { api.get('/companies').then((r) => setCompanies(r.data)); }, []);
  useEffect(() => {
    api.get('/transfer-requests/teammates').then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({ scope });
    if (companyId !== 'all') params.set('company_id', companyId);
    if (userId !== 'all') params.set('user_id', userId);
    if (scope === 'custom' && start && end) { params.set('start', start); params.set('end', end); }
    api.get(`/analytics/summary?${params.toString()}`).then((r) => setData(r.data));
    api.get(`/analytics/recruitment-cost?${params.toString()}`).then((r) => setCost(r.data)).catch(() => setCost(null));
  }, [scope, companyId, userId, start, end]);

  if (!data) return null;

  return (
    <>
      <PageHeader
        title="Analytics & Efficiency"
        description="Track calls, interviews, onboardings, payroll runs, and recruitment cost. Filter by date range, company or staff."
        actions={
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList data-testid="analytics-scope">
              <TabsTrigger value="today">Today</TabsTrigger>
              <TabsTrigger value="week">Week</TabsTrigger>
              <TabsTrigger value="month">Month</TabsTrigger>
              <TabsTrigger value="custom">Custom</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      >
        <div className="flex items-end gap-3 flex-wrap">
          {scope === 'custom' && (
            <>
              <div className="space-y-1"><Label className="text-xs">Start</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9" data-testid="analytics-start" /></div>
              <div className="space-y-1"><Label className="text-xs">End</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9" data-testid="analytics-end" /></div>
            </>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Company</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="h-9 w-56" data-testid="analytics-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Team member</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="h-9 w-56" data-testid="analytics-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All team members</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">{data.range?.start} → {data.range?.end}</p>
        </div>
      </PageHeader>

      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Calls" value={data.kpis.calls} icon={Phone} tone="primary" />
          <StatCard label="Interviews" value={data.kpis.interviews} icon={Video} tone="good" />
          <StatCard label="Emails" value={data.kpis.emails} icon={Send} />
          <StatCard label="Stage changes" value={data.kpis.stage_changes} icon={ClipboardList} />
          <StatCard label="Onboarding steps" value={data.kpis.onboardings} icon={Users} />
          <StatCard label="Payroll runs" value={data.kpis.payroll_runs} icon={Receipt} />
          <StatCard label="Jobs created" value={data.kpis.jobs_created || 0} icon={Users} />
          <StatCard label="Jobs approved" value={data.kpis.jobs_approved || 0} icon={ClipboardList} tone="good" />
          <StatCard label="Jobs closed" value={data.kpis.jobs_closed || 0} icon={Receipt} tone="primary" />
          <StatCard label="Avg days to close" value={data.kpis.avg_days_to_close != null ? `${data.kpis.avg_days_to_close}d` : '—'} icon={Send} />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="rounded-xl border bg-card p-4">
            <h3 className="font-display font-semibold">Activity per day</h3>
            <div className="h-72 mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.per_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tickLine={false} axisLine={false} fontSize={11} allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="calls" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="interviews" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="emails" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border bg-card p-4">
            <h3 className="font-display font-semibold">Per staff efficiency</h3>
            <div className="h-72 mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.per_staff}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="actor_name" tickLine={false} axisLine={false} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tickLine={false} axisLine={false} fontSize={11} allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="calls" stackId="a" fill="hsl(var(--chart-1))" />
                  <Bar dataKey="interviews" stackId="a" fill="hsl(var(--chart-3))" />
                  <Bar dataKey="emails" stackId="a" fill="hsl(var(--chart-2))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-display font-semibold">HR staff leaderboard</h3>
          <div className="rounded-xl border bg-card overflow-hidden mt-2">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Staff</TableHead><TableHead className="text-right">Calls</TableHead><TableHead className="text-right">Interviews</TableHead><TableHead className="text-right">Emails</TableHead><TableHead className="text-right">Stage changes</TableHead><TableHead className="text-right">Jobs created</TableHead><TableHead className="text-right">Jobs closed</TableHead><TableHead className="text-right">Total actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.per_staff.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">No activity yet in this range.</TableCell></TableRow>}
                {[...data.per_staff].sort((a, b) => b.total - a.total).map((s) => (
                  <TableRow key={s.actor_id} data-testid={`staff-row-${s.actor_id}`}>
                    <TableCell className="font-medium">{s.actor_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.calls}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.interviews}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.emails}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.stage_changes}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.jobs_created || 0}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.jobs_closed || 0}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{s.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* --- Recruitment cost analysis --- */}
        <div className="space-y-3" data-testid="recruitment-cost-section">
          <div className="flex items-center gap-2">
            <IndianRupee className="h-4 w-4 text-primary" />
            <h3 className="font-display font-semibold">Recruitment cost analysis</h3>
            <p className="text-xs text-muted-foreground">Cost per hire = (team salary ÷ working days) × time spent on hiring</p>
          </div>
          {cost ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Total cost" value={inr(cost.totals?.total_cost)} icon={IndianRupee} tone="primary" testId="stat-total-cost" />
                <StatCard label="Cost / hire" value={cost.totals?.cost_per_hire != null ? inr(cost.totals.cost_per_hire) : '—'} icon={TrendingUp} tone="good" testId="stat-cost-per-hire" />
                <StatCard label="Hires in range" value={cost.totals?.hires_count || 0} icon={Users} testId="stat-hires-count" />
                <StatCard label="Days spent" value={cost.totals?.total_days_spent || 0} icon={Clock} testId="stat-days-spent" hint={`${cost.totals?.activities_count || 0} activities`} />
              </div>

              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <p className="font-medium">Per team member</p>
                  <p className="text-[11px] text-muted-foreground">Configure salary + working days in Admin → Users</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team member</TableHead>
                      <TableHead className="text-right">Monthly salary</TableHead>
                      <TableHead className="text-right">Working days</TableHead>
                      <TableHead className="text-right">Activities</TableHead>
                      <TableHead className="text-right">Days spent</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(cost.per_staff || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No activity yet in this range.</TableCell></TableRow>}
                    {(cost.per_staff || []).map((s) => (
                      <TableRow key={s.actor_id} data-testid={`cost-row-${s.actor_id}`}>
                        <TableCell>
                          <p className="font-medium">{s.actor_name || '—'}</p>
                          <p className="text-xs text-muted-foreground">{s.email}</p>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{s.monthly_salary > 0 ? inr(s.monthly_salary) : <span className="text-muted-foreground">not set</span>}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.working_days_per_month}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.activities}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.days_spent}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{inr(s.cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {(cost.per_company || []).length > 0 && (
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="px-4 py-3 border-b">
                    <p className="font-medium">Hires by company</p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>Company</TableHead><TableHead className="text-right">Hires in range</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {cost.per_company.map((c) => (
                        <TableRow key={c.company_id}>
                          <TableCell>{c.company_name}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.hires}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Loading recruitment cost…</p>
          )}
        </div>
      </div>
    </>
  );
}
